import { useChecklists } from "./useJobs";

interface ChecklistSelectorProps {
    selectedChecklistId: string | null;
    onSelect: (checklistId: string, name: string) => void;
}

/**
 * `<select>` rendering checklists for the currently-selected project.
 * Loading / error / empty states are surfaced inline because this is
 * the blocking input — the export form can't be submitted without one.
 *
 * Visual: `f-label` + `f-select` family from `requirements/ui-patterns.md`.
 */
export function ChecklistSelector({ selectedChecklistId, onSelect }: ChecklistSelectorProps) {
    const { checklists, isLoading, error } = useChecklists();

    if (isLoading) {
        return (
            <div className="checklist-selector" data-testid="checklist-selector-loading">
                <i className="fa-solid fa-spinner fa-spin" aria-hidden="true" /> Loading checklists…
            </div>
        );
    }
    if (error) {
        return (
            <div className="callout callout-error" data-testid="checklist-selector-error">
                <i className="fa-solid fa-circle-exclamation" aria-hidden="true" />
                <span>Could not load checklists: {error.message}</span>
            </div>
        );
    }
    if (checklists.length === 0) {
        return (
            <div className="callout callout-info" data-testid="checklist-selector-empty">
                <i className="fa-solid fa-circle-info" aria-hidden="true" />
                <span>No checklists available in this project.</span>
            </div>
        );
    }

    return (
        <div className="field">
            <label className="f-label" htmlFor="checklist-select">
                Checklist
            </label>
            <select
                id="checklist-select"
                className="f-select"
                data-testid="checklist-selector-select"
                value={selectedChecklistId ?? ""}
                onChange={(e) => {
                    const id = e.target.value;
                    const match = checklists.find((c) => c.id === id);
                    if (!match) return;
                    onSelect(match.id, match.name);
                }}
            >
                <option value="" disabled>
                    Select a checklist…
                </option>
                {checklists.map((c) => (
                    <option key={c.id} value={c.id}>
                        {c.name}
                    </option>
                ))}
            </select>
        </div>
    );
}
